import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, BookOpen, FileText, Lightbulb, MessageSquareText, Rocket, Sprout, Target, UsersRound } from "lucide-react";
import { GROWTH_REQUESTS, GROWTH_UNREAD_NOTIFICATIONS } from "../../../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../../../utils/Storage.js";
import { getApi, putApi } from "../../../../../utils/api.ts";
import { showError } from "../../../../../utils/toast.ts";
import { useUser } from "../../../../../lib/rbac/useUser.ts";
import { Card } from "../../../../shared/card/card.tsx";
import type { GrowthCategory, GrowthRequest } from "../../../../../pages/growth-activities/growth-types.ts";
import illustration from "./woman-coding.png";
import styles from "./growth-initiatives-card.module.css";

type Notification = {
  requestId: number;
  requestCategory: GrowthCategory;
  totalNotificationCount: number;
  noteType: string;
  isViewed: boolean;
};

type NotificationSummary = {
  totalNotificationCount: number;
  hasRequests: boolean;
  activeRequestCount?: number;
  notifications: Notification[];
  requests?: Array<{ requestId: number; requestStatus: string; requestType: GrowthCategory }>;
  message: string;
};

type DashboardRequest = Pick<GrowthRequest, "id" | "growthCategory" | "growthStatus" | "displayTitle">;

type Note = {
  id: number;
  requestId: number;
  employeeId?: number;
  employeeName?: string | null;
  noteType: string;
  comments: string;
  updatedAt: string | null;
  isViewed?: boolean;
};

const categories = {
  CONTRIBUTION: { label: "Contribute", title: "Career growth request", Icon: Target, tone: "blue" },
  LEARNING: { label: "Learn & Upskill", title: "Learning request", Icon: BookOpen, tone: "purple" },
  CERTIFICATION: { label: "Certification", title: "Certification request", Icon: Award, tone: "blue" },
  INNOVATION: { label: "Innovate & Improve", title: "Innovation request", Icon: Lightbulb, tone: "amber" },
  LAUNCHING: { label: "Build & Launch", title: "Build and launch request", Icon: Rocket, tone: "blue" },
  KNOWLEDGE_SHARING: { label: "Host & Share Knowledge", title: "Knowledge sharing request", Icon: UsersRound, tone: "purple" },
} as const;

const statuses: Record<string, string> = {
  RECEIVED: "Received",
  BEING_REVIEWED: "In review",
  IN_PROGRESS: "In progress",
  WAITING_EMPLOYEE: "Awaiting response",
  COMPLETED: "Completed",
  CLOSED: "Closed",
};

const growthPath = "/dashboard/elevateYourself";

function recentTime(value: string | null | undefined) {
  if (!value) return "";
  const elapsed = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(elapsed) || elapsed < 0) return "";
  const minutes = Math.floor(elapsed / 60000);
  if (minutes < 60) return `${Math.max(1, minutes)}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function GrowthInitiativesCard() {
  const employeeId = useUser()?.id;
  const [summary, setSummary] = useState<NotificationSummary | null>(null);
  const [requests, setRequests] = useState<DashboardRequest[]>([]);
  const [notesByRequest, setNotesByRequest] = useState<Record<number, Note[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const token = getAuthToken("accessToken");
    setLoading(true);
    setError("");
    async function load() {
      try {
        const response = await getApi<NotificationSummary>(GROWTH_UNREAD_NOTIFICATIONS, token, "json", controller.signal);
        const result = response.data;
        if (typeof result?.hasRequests !== "boolean" || !Array.isArray(result.notifications)) {
          throw new Error("Could not load Growth & Initiatives updates.");
        }
        if (controller.signal.aborted) return;
        setSummary(result);
        if (!result.hasRequests) {
          setRequests([]);
          setNotesByRequest({});
          return;
        }

        const requestLoad: Promise<DashboardRequest[]> = Array.isArray(result.requests)
          ? Promise.resolve(result.requests.map((request) => ({
            id: request.requestId,
            growthCategory: request.requestType,
            growthStatus: request.requestStatus,
            displayTitle: "",
          })))
          : getApi<GrowthRequest[]>(GROWTH_REQUESTS, token, "json", controller.signal)
            .then(({ data }) => Array.isArray(data) ? data : [])
            .catch(() => [] as GrowthRequest[]);
        const notificationRows = result.notifications.filter((item) => Number.isInteger(item.requestId) && item.requestId > 0).slice(0, 2);
        const noteLoad = Promise.all(notificationRows.map(async ({ requestId }) => {
          try {
            const { data } = await getApi<Note[]>(`${GROWTH_REQUESTS}/${requestId}/notes`, token, "json", controller.signal);
            return [requestId, Array.isArray(data) ? data.filter((note) => note.noteType !== "INTERNAL_NOTE") : []] as const;
          } catch {
            return [requestId, [] as Note[]] as const;
          }
        }));
        const [requestList, noteEntries] = await Promise.all([requestLoad, noteLoad]);
        if (controller.signal.aborted) return;
        setRequests(requestList);
        setNotesByRequest(Object.fromEntries(noteEntries));
      } catch (cause) {
        if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Could not load Growth & Initiatives updates.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    void load();
    return () => controller.abort();
  }, [attempt]);

  const activeRequests = requests.filter((item) => item.growthStatus !== "COMPLETED" && item.growthStatus !== "CLOSED");
  const activeRequestCount = summary?.activeRequestCount ?? activeRequests.length;
  const hasNewNotes = Boolean(summary?.notifications.length);
  const requestPreview = (activeRequests.length ? activeRequests : requests).slice(0, 2);

  function markConversationViewed(requestId: number) {
    void putApi(`${GROWTH_REQUESTS}/${requestId}/notes/viewed`, getAuthToken("accessToken"), {})
      .catch(() => showError("Could not mark this conversation as viewed."));
  }

  return <Card className={styles.card} padding="3xl" aria-label="Growth & Initiatives">
    <header className={styles.header}>
      <span className={styles.headerIcon}><Sprout size={19} aria-hidden="true" /></span>
      <div className={styles.headerCopy}><h2>Growth & Initiatives</h2><p>{hasNewNotes ? "Recent conversation updates" : summary?.hasRequests ? "Track your requests and updates" : "Stay connected. Keep growing."}</p></div>
      {!loading && !error && summary?.hasRequests ? <span className={`${styles.countBadge} ${hasNewNotes ? styles.newBadge : ""}`}>{hasNewNotes ? `${summary.totalNotificationCount} new` : `${activeRequestCount} active`}</span> : null}
    </header>

    {loading ? <div className={styles.state} role="status">Loading your growth updates…</div> : null}
    {!loading && error ? <div className={styles.state} role="alert"><p>{error}</p><button type="button" onClick={() => setAttempt((current) => current + 1)}>Try again</button></div> : null}

    {!loading && !error && !summary?.hasRequests ? <div className={styles.emptyState}>
      <img src={illustration} alt="Employee exploring ideas while working on a laptop" />
      <strong>Start your growth journey</strong>
      <p>Share your first initiative and connect with the people who can help you grow.</p>
      <Link className={styles.actionLink} to={growthPath}><Sprout size={15} aria-hidden="true" />Explore Growth & Initiatives<ArrowRight size={15} aria-hidden="true" /></Link>
    </div> : null}

    {!loading && !error && summary?.hasRequests && !hasNewNotes ? <div className={styles.emptyState}>
      <img src={illustration} alt="Employee exploring ideas while working on a laptop" />
      <strong>{activeRequestCount > 0 ? "Your requests are active" : "Your requests are up to date"}</strong>
      <p>No unread messages right now. We’ll let you know when a conversation has an update.</p>
      {requestPreview.length ? <div className={styles.requestPills}>{requestPreview.map((request) => {
        const category = categories[request.growthCategory];
        return <Link key={request.id} to={`${growthPath}?requestId=${request.id}`}><category.Icon size={14} aria-hidden="true" /><span>{request.displayTitle || category.title}</span><small>{statuses[request.growthStatus] || request.growthStatus.replaceAll("_", " ")}</small></Link>;
      })}</div> : null}
      <Link className={styles.actionLink} to={growthPath}><FileText size={15} aria-hidden="true" />View Requests<ArrowRight size={15} aria-hidden="true" /></Link>
    </div> : null}

    {!loading && !error && summary?.hasRequests && hasNewNotes ? <div className={styles.updates}>
      {summary.notifications.slice(0, 2).map((notification) => {
        const request = requests.find((item) => item.id === notification.requestId);
        const category = categories[notification.requestCategory];
        const notes = [...(notesByRequest[notification.requestId] || [])].sort((a, b) => b.id - a.id);
        const latest = notes.find((note) => note.isViewed === false && note.employeeId !== employeeId) || notes.find((note) => note.employeeId !== employeeId);
        const ownReply = notes.find((note) => note.employeeId === employeeId);
        return <Link key={notification.requestId} className={styles.update} to={`${growthPath}?requestId=${notification.requestId}`} onClick={() => markConversationViewed(notification.requestId)}>
          <span className={`${styles.updateIcon} ${styles[category.tone]}`}><category.Icon size={18} aria-hidden="true" /></span>
          <span className={styles.updateCopy}>
            <span className={styles.updateHeading}><strong>{request?.displayTitle || category.title}</strong><span>{notification.totalNotificationCount} new {notification.totalNotificationCount === 1 ? "note" : "notes"}</span>{request ? <em>{statuses[request.growthStatus] || request.growthStatus.replaceAll("_", " ")}</em> : null}</span>
            {latest ? <span className={styles.preview}>{latest.comments}<small>{recentTime(latest.updatedAt)}</small></span> : <span className={styles.preview}>Open to read the latest update</span>}
            {ownReply && ownReply.id !== latest?.id ? <span className={styles.ownReply}><b>You</b><span>{ownReply.comments}</span><small>{recentTime(ownReply.updatedAt)}</small></span> : null}
          </span>
        </Link>;
      })}
      <Link className={styles.actionLink} to={growthPath}><MessageSquareText size={15} aria-hidden="true" />View Conversations<ArrowRight size={15} aria-hidden="true" /></Link>
    </div> : null}
  </Card>;
}
